import React, {
  useRef,
  useState,
  RefObject,
  useEffect,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import { Grid } from '@mui/material';

import DateRangePicker, {
  Ref as DateRangePickerRef,
} from './components/DateRangePicker';
import DateRangeCalendar, {
  Ref as DateRangeCalendarRef,
} from './components/DateRangeCalendar';
import DataGranularities, {
  Ref as DataGranularitiesRef,
} from './components/DataGranularities';
import PreDefinedCalendarFilters, {
  Ref as PreDefinedCalendarFiltersRef,
} from './components/PreDefinedCalendarFilters';
import DataSpecificAndCustomFilter, {
  Ref as DataSpecificAndCustomFilterRef,
} from './components/DataSpecificAndCustomFilter';
import Modal, { Ref as ModalRef } from './components/Modal';

import {
  formatDate,
  getFormattedValue,
  getHourInLocalTimeZone,
  CustomGranularityFilter,
  allAvailableGranularities,
  getFormatHoursWithTimeZone,
} from './helper';
import {
  GlobalDateFilter,
  CalendarFilterOption,
  DateRangePickerFilterVariables,
  PreDefinedGranularityFilterOption,
} from 'types/types';

interface Props {
  dateFrom?: Date;
  dateTo?: Date;
  selectedGranularity?: string;
  selectedFilterOption?: string;
  customFilteredEnteredValues?: {
    daysFrom: number;
    daysTo: number;
  };
  selectedValuesSpecific?: string[];
  calendarFilterOptions: {
    [key: string]: CalendarFilterOption[];
  };
  selectedValuesCustom?: CustomGranularityFilter;
  granularityOptions: PreDefinedGranularityFilterOption[];
  handleApplyFilter: (filterData: GlobalDateFilter) => void;
  inputBody?: GlobalDateFilter;
}

export interface Ref {
  handleShowModal: (show: boolean) => void;
  dateRangePickerRef: RefObject<DateRangePickerRef>;
  dataGranularitiesRef: RefObject<DataGranularitiesRef>;
  dateRangeCalendarRef: RefObject<DateRangeCalendarRef>;
  preDefinedCalendarFiltersRef: RefObject<PreDefinedCalendarFiltersRef>;
  dataSpecificAndCustomFilterRef: RefObject<DataSpecificAndCustomFilterRef>;
}

const GranularityDateRangePicker: ForwardRefRenderFunction<Ref, Props> = (
  {
    dateTo,
    dateFrom,
    inputBody,
    handleApplyFilter,
    granularityOptions,
    selectedGranularity,
    selectedFilterOption,
    selectedValuesCustom,
    calendarFilterOptions,
    selectedValuesSpecific,
    customFilteredEnteredValues,
  },
  ref
) => {
  const modalRef = useRef<ModalRef>(null);
  const preDefinedCalendarFiltersRef =
    useRef<PreDefinedCalendarFiltersRef>(null);
  const dataSpecificAndCustomFilterRef =
    useRef<DataSpecificAndCustomFilterRef>(null);
  const dateRangePickerRef = useRef<DateRangePickerRef>(null);
  const dataGranularitiesRef = useRef<DataGranularitiesRef>(null);
  const dateRangeCalendarRef = useRef<DateRangeCalendarRef>(null);

  const [selectedFilterVariables, setSelectedFilterVariables] =
    useState<DateRangePickerFilterVariables>();

  const handleShowModal = (show: boolean) =>
    modalRef.current?.handleShowModal(show);

  const formatApplyBody = (): GlobalDateFilter => {
    const values: {
      id: string;
      name: string;
      items?: { id: string; name: any }[];
    }[] = [];
    const filterData: GlobalDateFilter = {
      filterId: 'global_date_filter',
      values: values,
    };

    const selectedGranularity =
      dataGranularitiesRef.current?.selectedGranularity;
    const selectedFilterOption =
      preDefinedCalendarFiltersRef.current?.selectedOption;
    const customValues =
      preDefinedCalendarFiltersRef.current?.preDefinedCalendarCustomFilterRef
        .current?.enteredValues;
    const specificDataValues =
      dataSpecificAndCustomFilterRef.current?.specificFilter.current
        ?.selectedValues;
    const customDataValues =
      dataSpecificAndCustomFilterRef.current?.customFilter.current
        ?.selectedValues;
    const calendarDates = dateRangeCalendarRef.current?.selectedDates;

    let granularityLevel = '';

    if (selectedGranularity?.value.toLowerCase() == 'weekly') {
      granularityLevel = 'weeks';
    } else if (selectedGranularity?.value.toLowerCase() == 'monthly') {
      granularityLevel = 'months';
    } else {
      granularityLevel = 'days';
    }

    if (selectedFilterOption) {
      if (selectedFilterOption.label.toLowerCase() !== 'custom') {
        filterData.values?.push({
          id: selectedFilterOption.value,
          name: selectedFilterOption.label,
        });
      }
    } else if (
      customValues &&
      (+customValues.daysFrom > 0 || +customValues.daysTo > 0)
    ) {
      filterData.values?.push({
        id: `CD: ${customValues?.daysFrom}~${customValues?.daysTo}:${granularityLevel}`,
        name: `From ${customValues?.daysFrom} to ${customValues?.daysTo} ${granularityLevel} ago`,
      });
    }

    if (
      calendarDates &&
      !selectedFilterOption &&
      (!customValues ||
        (customValues &&
          +customValues.daysFrom <= 0 &&
          +customValues.daysTo <= 0))
    ) {
      calendarDates[0].setHours(0, 0, 0, 0);
      calendarDates[1].setHours(23, 59, 59, 0);

      if (customDataValues) {
        calendarDates[0].setHours(
          customDataValues.startTime.hour,
          customDataValues.startTime.minute,
          0,
          0
        );
        calendarDates[1].setHours(
          customDataValues.endTime.hour,
          customDataValues.endTime.minute,
          0,
          0
        );
      }

      const granularity = selectedGranularity?.value;

      filterData.values?.push({
        id: `${formatDate(calendarDates[0])}|${formatDate(calendarDates[1])}`,
        name: `${getFormattedValue(
          calendarDates[0],
          granularity
        )} To ${getFormattedValue(calendarDates[1], granularity)}`,
      });
    }

    if (selectedGranularity) {
      filterData.values?.push({
        id: selectedGranularity.value,
        name: selectedGranularity.label,
      });
    }

    if (customDataValues) {
      let id = `${customDataValues.startTime.hour}:${customDataValues.startTime.minute}|${customDataValues.endTime.hour}:${customDataValues.endTime.minute}`;
      let name = `Custom time from (${customDataValues.startTime.hour}:${customDataValues.startTime.minute} to ${customDataValues.endTime.hour}:${customDataValues.endTime.minute})`;

      if (selectedGranularity?.value.toLowerCase() === 'hourly') {
        id = `${customDataValues.startTime.hour}|${customDataValues.endTime.hour}`;
        name = `Custom time from (${customDataValues.startTime.hour} to ${customDataValues.endTime.hour})`;
      }

      filterData.values?.push({
        id: id,
        name: name,
      });
    }

    if (specificDataValues && specificDataValues.length > 0) {
      const filterDataWithTimezone = getFormatHoursWithTimeZone(
        specificDataValues.map((el) => el.value)
      );
      filterData.values?.push({
        id: `specific`,
        name: `Specific Hours`,
        items: filterDataWithTimezone.map((el) => ({
          id: `F:${el}`,
          name: +el,
        })),
      });
    }

    return filterData;
  };

  const extractVariablesFromFilterData = (
    filterData: GlobalDateFilter
  ): DateRangePickerFilterVariables => {
    const variables: DateRangePickerFilterVariables = {};

    filterData.values?.forEach((filter) => {
      // Extract granularity level (weeks, months, days)
      if (
        allAvailableGranularities.find((el) => el == filter.id.toLowerCase())
      ) {
        variables.selectedGranularity = filter.id;
      }

      // Extract predefined filter option (non-custom)
      if (
        !filter.id.startsWith('CD:') &&
        !filter.id.match(/\|/) &&
        filter.id.match(/^[a-zA-Z]+:\d+$/)
      ) {
        variables.selectedFilterOption = filter.id;
      }

      // Extract specific data values
      if (filter.id === 'specific' && filter.items) {
        const hours = filter.items.map((el) => el.name);
        const formattedHours = getHourInLocalTimeZone(hours);
        variables.specificDataValues = formattedHours.map((item) => ({
          value: parseInt(item, 10),
        }));
      }

      // Extract custom data values (start and end times)
      if (
        filter.id.includes('|') &&
        filter.id.match(/^\d{1,2}:\d{1,2}\|\d{1,2}:\d{1,2}$/)
      ) {
        const [start, end] = filter.id.split('|');
        const [startHour, startMinute] = start.split(':').map(Number);
        const [endHour, endMinute] = end.split(':').map(Number);

        variables.customDataValues = {
          startTime: { hour: startHour, minute: startMinute },
          endTime: { hour: endHour, minute: endMinute },
        };
      }

      // Extract calendar dates
      if (filter.id.includes('|') && filter.id.match(/\d{4}-\d{2}-\d{2}/)) {
        const [startDate, endDate] = filter.id.split('|');
        variables.calendarDates = [new Date(startDate), new Date(endDate)];
      }

      // Extract custom values for predefined filters
      if (
        filter.id.startsWith('CD:') &&
        filter.id.match(/CD:\s*(\d+)~(\d+):(\w+)/)
      ) {
        const match = filter.id.match(/CD:\s*(\d+)~(\d+):(\w+)/);
        if (match) {
          variables.customValues = {
            daysFrom: +match[1],
            daysTo: +match[2],
          };
        }
        setTimeout(() => {
          preDefinedCalendarFiltersRef.current?.handleShowCustomOption(true);
        }, 100);
      }
    });

    return variables;
  };

  const handleApply = () => {
    const filterData = formatApplyBody();
    handleApplyFilter(filterData);
    modalRef.current?.handleShowModal(false);
  };

  useImperativeHandle(ref, () => ({
    handleShowModal,
    dateRangePickerRef,
    dataGranularitiesRef,
    dateRangeCalendarRef,
    preDefinedCalendarFiltersRef,
    dataSpecificAndCustomFilterRef,
  }));

  useEffect(() => {
    if (inputBody) {
      const filterVariables = extractVariablesFromFilterData(inputBody);
      setSelectedFilterVariables(filterVariables);
    }
  }, [inputBody]);

  return (
    <div className="granularity-date-range-picker-container">
      <Modal
        title="Select Date"
        ref={modalRef}
        width="lg"
        handleApply={handleApply}
      >
        <Grid container>
          <Grid item xs={6} sx={{ paddingRight: '1.5rem' }}>
            <Grid container>
              <Grid item xs={12}>
                <DataGranularities
                  ref={dataGranularitiesRef}
                  dataSpecificAndCustomFilterRef={
                    dataSpecificAndCustomFilterRef
                  }
                  dateRangePickerRef={dateRangePickerRef}
                  granularityOptions={granularityOptions}
                  dateRangeCalendarRef={dateRangeCalendarRef}
                  selectedGranularityValue={
                    selectedFilterVariables?.selectedGranularity ||
                    selectedGranularity
                  }
                  preDefinedCalendarFiltersRef={preDefinedCalendarFiltersRef}
                />
              </Grid>
              <Grid item xs={12} sx={{ marginTop: '1rem' }}>
                <DataSpecificAndCustomFilter
                  ref={dataSpecificAndCustomFilterRef}
                  selectedValuesCustom={
                    selectedFilterVariables?.customDataValues ||
                    selectedValuesCustom
                  }
                  selectedValuesSpecific={
                    selectedFilterVariables?.specificDataValues?.map((el) =>
                      el.value.toString()
                    ) || selectedValuesSpecific
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Grid container>
              <Grid item xs={12}>
                <Grid item xs={12} sx={{ marginBottom: '1rem' }}>
                  <DateRangePicker
                    dateTo={
                      selectedFilterVariables?.calendarDates?.[1] || dateTo
                    }
                    dateFrom={
                      selectedFilterVariables?.calendarDates?.[0] || dateFrom
                    }
                    ref={dateRangePickerRef}
                    dateRangeCalendarRef={dateRangeCalendarRef}
                    dataGranularitiesRef={dataGranularitiesRef}
                    preDefinedCalendarFiltersRef={preDefinedCalendarFiltersRef}
                  />
                </Grid>
                <Grid item xs={12} sx={{ marginBottom: '1rem' }}>
                  <DateRangeCalendar
                    dateTo={
                      selectedFilterVariables?.calendarDates?.[1] || dateTo
                    }
                    dateFrom={
                      selectedFilterVariables?.calendarDates?.[0] || dateFrom
                    }
                    ref={dateRangeCalendarRef}
                    dateRangePickerRef={dateRangePickerRef}
                    dataGranularitiesRef={dataGranularitiesRef}
                    preDefinedCalendarFiltersRef={preDefinedCalendarFiltersRef}
                  />
                </Grid>
                <Grid item xs={12}>
                  <PreDefinedCalendarFilters
                    ref={preDefinedCalendarFiltersRef}
                    dateRangePickerRef={dateRangePickerRef}
                    selectedFilterOption={
                      selectedFilterVariables?.selectedFilterOption ||
                      selectedFilterOption
                    }
                    dateRangeCalendarRef={dateRangeCalendarRef}
                    dataGranularitiesRef={dataGranularitiesRef}
                    customFilteredEnteredValues={
                      selectedFilterVariables?.customValues ||
                      customFilteredEnteredValues
                    }
                    calendarFilterOptions={calendarFilterOptions}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Modal>
    </div>
  );
};

export default forwardRef<Ref, Props>(GranularityDateRangePicker);
